import { useState, useEffect, useCallback } from 'react';
import { 
  Wallet, Search, Eye, Plus, CreditCard, TrendingUp, TrendingDown,
  Store, RefreshCw, FileText, DollarSign, Calendar, ChevronRight, AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ledgersAPI, storesAPI } from '@/services/api';

const paymentModes = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'upi', label: 'UPI' },
  { value: 'rtgs', label: 'RTGS' },
  { value: 'neft', label: 'NEFT' }
];

const entryTypeColors = {
  invoice: 'bg-blue-100 text-blue-700',
  payment: 'bg-emerald-100 text-emerald-700',
  credit_note: 'bg-teal-100 text-teal-700',
  debit_note: 'bg-orange-100 text-orange-700',
  adjustment: 'bg-gray-100 text-gray-700',
  opening_balance: 'bg-purple-100 text-purple-700'
};

const FranchiseLedgerPage = () => {
  const [ledgers, setLedgers] = useState([]);
  const [franchiseStores, setFranchiseStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Selected ledger for detail view
  const [selectedLedger, setSelectedLedger] = useState(null);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Payment dialog
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState({
    franchise_store_id: '',
    payment_date: new Date().toISOString().split('T')[0],
    payment_mode: 'bank_transfer',
    amount: '',
    bank_name: '',
    cheque_number: '',
    transaction_reference: '',
    notes: ''
  });
  const [processing, setProcessing] = useState(false);
  
  // Initialize ledger dialog
  const [initDialogOpen, setInitDialogOpen] = useState(false);
  const [initForm, setInitForm] = useState({
    store_id: '',
    opening_balance: 0,
    credit_limit: 0
  });
  
  const { toast } = useToast();

  const fetchLedgers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await ledgersAPI.listFranchiseLedgers({ page, page_size: 20, search });
      setLedgers(response.data.items || []);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch ledgers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [page, search, toast]);

  const fetchFranchiseStores = useCallback(async () => {
    try {
      const response = await storesAPI.list({ is_active: true });
      const franchises = (response.data || []).filter(s => s.store_type === 'franchise');
      setFranchiseStores(franchises);
    } catch (error) {
      console.error('Failed to fetch franchise stores:', error);
    }
  }, []);

  useEffect(() => {
    fetchLedgers();
    fetchFranchiseStores();
  }, [fetchLedgers, fetchFranchiseStores]);

  const handleOpenDetail = async (ledger) => {
    setSelectedLedger(ledger);
    setDetailDialogOpen(true);
    setEntriesLoading(true);
    
    try {
      const response = await ledgersAPI.getFranchiseLedgerEntries(ledger.franchise_store_id, { page_size: 100 });
      setLedgerEntries(response.data.items || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch ledger entries', variant: 'destructive' });
    } finally {
      setEntriesLoading(false);
    }
  };

  const handleOpenPayment = (ledger = null) => {
    setPaymentForm({
      franchise_store_id: ledger?.franchise_store_id || '',
      payment_date: new Date().toISOString().split('T')[0],
      payment_mode: 'bank_transfer',
      amount: '',
      bank_name: '',
      cheque_number: '',
      transaction_reference: '',
      notes: ''
    });
    setPaymentDialogOpen(true);
  };

  const handleCreatePayment = async () => {
    if (!paymentForm.franchise_store_id || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast({ title: 'Error', description: 'Please fill all required fields', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await ledgersAPI.createFranchisePayment({
        ...paymentForm,
        amount: parseFloat(paymentForm.amount)
      });
      
      toast({ title: 'Success', description: 'Payment recorded successfully' });
      setPaymentDialogOpen(false);
      fetchLedgers();
      
      if (selectedLedger) {
        handleOpenDetail(selectedLedger);
      }
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to create payment', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const handleInitializeLedger = async () => {
    if (!initForm.store_id) {
      toast({ title: 'Error', description: 'Please select a store', variant: 'destructive' });
      return;
    }

    setProcessing(true);
    try {
      await ledgersAPI.initializeFranchiseLedger(initForm.store_id, {
        opening_balance: parseFloat(initForm.opening_balance) || 0,
        credit_limit: parseFloat(initForm.credit_limit) || 0
      });
      
      toast({ title: 'Success', description: 'Ledger initialized successfully' });
      setInitDialogOpen(false);
      fetchLedgers();
    } catch (error) {
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to initialize ledger', 
        variant: 'destructive' 
      });
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount || 0);
  };

  const filteredLedgers = ledgers.filter(l => 
    !search || 
    l.franchise_store_name?.toLowerCase().includes(search.toLowerCase()) ||
    l.franchise_store_code?.toLowerCase().includes(search.toLowerCase())
  );

  const totalOutstanding = ledgers.reduce((sum, l) => sum + (l.current_balance || 0), 0);

  // Get stores without ledger
  const storesWithoutLedger = franchiseStores.filter(
    store => !ledgers.find(l => l.franchise_store_id === store.id)
  );

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Wallet className="w-6 h-6 text-orange-600" />
            Franchise Ledger
          </h1>
          <p className="text-gray-500 mt-1">Manage franchise store balances and payments</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={fetchLedgers} className="border-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button onClick={() => handleOpenPayment()} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            <Plus className="w-4 h-4 mr-2" /> Record Payment
          </Button>
          {storesWithoutLedger.length > 0 && (
            <Button onClick={() => setInitDialogOpen(true)} className="bg-orange-600 hover:bg-orange-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Initialize Ledger
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Franchises</p>
                <p className="text-2xl font-bold text-gray-800">{ledgers.length}</p>
              </div>
              <Store className="w-8 h-8 text-orange-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Outstanding</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalOutstanding)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-red-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Credit Blocked</p>
                <p className="text-2xl font-bold text-amber-600">
                  {ledgers.filter(l => l.credit_blocked).length}
                </p>
              </div>
              <AlertTriangle className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-gray-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(ledgers.reduce((sum, l) => sum + (l.total_paid || 0), 0))}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="bg-white border-gray-200 mb-6">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input 
              placeholder="Search by franchise name or code..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-gray-50 border-gray-300"
            />
          </div>
        </CardContent>
      </Card>

      {/* Ledgers Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading ledgers...</div>
          ) : filteredLedgers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No franchise ledgers found</p>
              {storesWithoutLedger.length > 0 && (
                <Button onClick={() => setInitDialogOpen(true)} className="mt-4 bg-orange-600 hover:bg-orange-700 text-white">
                  Initialize Ledger
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-700">Franchise</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Outstanding</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Credit Limit</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Available</TableHead>
                  <TableHead className="font-semibold text-gray-700">Status</TableHead>
                  <TableHead className="font-semibold text-gray-700">Last Payment</TableHead>
                  <TableHead className="font-semibold text-gray-700 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredLedgers.map(ledger => (
                  <TableRow key={ledger.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-orange-500" />
                        <div>
                          <p className="font-medium text-gray-800">{ledger.franchise_store_name}</p>
                          <p className="text-xs text-gray-500">{ledger.franchise_store_code}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-bold ${ledger.current_balance > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                        {formatCurrency(ledger.current_balance)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right text-gray-600">
                      {formatCurrency(ledger.credit_limit)}
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={ledger.available_credit < 0 ? 'text-red-600' : 'text-emerald-600'}>
                        {formatCurrency(ledger.available_credit)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {ledger.credit_blocked ? (
                        <Badge className="bg-red-100 text-red-700">Blocked</Badge>
                      ) : ledger.current_balance > ledger.credit_limit ? (
                        <Badge className="bg-amber-100 text-amber-700">Over Limit</Badge>
                      ) : (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-gray-500">
                        {ledger.last_payment_date ? new Date(ledger.last_payment_date).toLocaleDateString('en-IN') : '-'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleOpenDetail(ledger)}
                          className="text-gray-600 hover:text-orange-600"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="sm"
                          onClick={() => handleOpenPayment(ledger)}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        >
                          <Plus className="w-4 h-4 mr-1" /> Payment
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Store className="w-5 h-5 text-orange-600" />
              {selectedLedger?.franchise_store_name} - Ledger
            </DialogTitle>
          </DialogHeader>
          
          {selectedLedger && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-4 gap-4">
                <div className="p-3 bg-red-50 rounded-lg">
                  <Label className="text-xs text-red-600">Outstanding</Label>
                  <p className="text-xl font-bold text-red-700">{formatCurrency(selectedLedger.current_balance)}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500">Credit Limit</Label>
                  <p className="text-xl font-bold text-gray-700">{formatCurrency(selectedLedger.credit_limit)}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg">
                  <Label className="text-xs text-emerald-600">Available Credit</Label>
                  <p className="text-xl font-bold text-emerald-700">{formatCurrency(selectedLedger.available_credit)}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg">
                  <Label className="text-xs text-blue-600">Total Paid</Label>
                  <p className="text-xl font-bold text-blue-700">{formatCurrency(selectedLedger.total_paid)}</p>
                </div>
              </div>

              {/* Ledger Entries */}
              <div>
                <Label className="text-gray-700 font-medium mb-2 block">Ledger Statement</Label>
                {entriesLoading ? (
                  <div className="text-center py-8 text-gray-500">Loading entries...</div>
                ) : ledgerEntries.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No entries found</div>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead>Reference</TableHead>
                          <TableHead className="text-right">Debit</TableHead>
                          <TableHead className="text-right">Credit</TableHead>
                          <TableHead className="text-right">Balance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {ledgerEntries.map((entry, idx) => (
                          <TableRow key={idx}>
                            <TableCell className="text-sm">
                              {entry.entry_date ? new Date(entry.entry_date).toLocaleDateString('en-IN') : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge className={entryTypeColors[entry.entry_type] || 'bg-gray-100'}>
                                {entry.entry_type?.replace('_', ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-700">{entry.description}</TableCell>
                            <TableCell className="text-sm text-gray-500">{entry.reference_number || '-'}</TableCell>
                            <TableCell className="text-right text-red-600">
                              {entry.debit_amount > 0 ? formatCurrency(entry.debit_amount) : '-'}
                            </TableCell>
                            <TableCell className="text-right text-emerald-600">
                              {entry.credit_amount > 0 ? formatCurrency(entry.credit_amount) : '-'}
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(entry.running_balance)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <CreditCard className="w-5 h-5 text-emerald-600" />
              Record Franchise Payment
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Franchise Store *</Label>
              <Select 
                value={paymentForm.franchise_store_id} 
                onValueChange={(v) => setPaymentForm(f => ({ ...f, franchise_store_id: v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue placeholder="Select franchise" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {ledgers.map(l => (
                    <SelectItem key={l.franchise_store_id} value={l.franchise_store_id}>
                      {l.franchise_store_name} ({l.franchise_store_code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Payment Date *</Label>
                <Input 
                  type="date"
                  value={paymentForm.payment_date}
                  onChange={(e) => setPaymentForm(f => ({ ...f, payment_date: e.target.value }))}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Amount *</Label>
                <Input 
                  type="number"
                  min="0"
                  step="0.01"
                  value={paymentForm.amount}
                  onChange={(e) => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                  placeholder="0.00"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Payment Mode *</Label>
              <Select 
                value={paymentForm.payment_mode} 
                onValueChange={(v) => setPaymentForm(f => ({ ...f, payment_mode: v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {paymentModes.map(mode => (
                    <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {paymentForm.payment_mode === 'cheque' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-gray-700">Bank Name</Label>
                  <Input 
                    value={paymentForm.bank_name}
                    onChange={(e) => setPaymentForm(f => ({ ...f, bank_name: e.target.value }))}
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-700">Cheque Number</Label>
                  <Input 
                    value={paymentForm.cheque_number}
                    onChange={(e) => setPaymentForm(f => ({ ...f, cheque_number: e.target.value }))}
                    className="bg-gray-50 border-gray-300"
                  />
                </div>
              </div>
            )}

            {['bank_transfer', 'upi', 'rtgs', 'neft'].includes(paymentForm.payment_mode) && (
              <div className="space-y-2">
                <Label className="text-gray-700">Transaction Reference</Label>
                <Input 
                  value={paymentForm.transaction_reference}
                  onChange={(e) => setPaymentForm(f => ({ ...f, transaction_reference: e.target.value }))}
                  placeholder="UTR / Reference Number"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-gray-700">Notes</Label>
              <Textarea 
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentDialogOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleCreatePayment} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {processing ? 'Processing...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Initialize Ledger Dialog */}
      <Dialog open={initDialogOpen} onOpenChange={setInitDialogOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Plus className="w-5 h-5 text-orange-600" />
              Initialize Franchise Ledger
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-700">Franchise Store *</Label>
              <Select 
                value={initForm.store_id} 
                onValueChange={(v) => setInitForm(f => ({ ...f, store_id: v }))}
              >
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue placeholder="Select franchise store" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {storesWithoutLedger.map(store => (
                    <SelectItem key={store.id} value={store.id}>
                      {store.name} ({store.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {storesWithoutLedger.length === 0 && (
                <p className="text-sm text-amber-600">All franchise stores have ledgers initialized</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Opening Balance</Label>
                <Input 
                  type="number"
                  value={initForm.opening_balance}
                  onChange={(e) => setInitForm(f => ({ ...f, opening_balance: e.target.value }))}
                  placeholder="0.00"
                  className="bg-gray-50 border-gray-300"
                />
                <p className="text-xs text-gray-500">Positive = owes HQ</p>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Credit Limit</Label>
                <Input 
                  type="number"
                  min="0"
                  value={initForm.credit_limit}
                  onChange={(e) => setInitForm(f => ({ ...f, credit_limit: e.target.value }))}
                  placeholder="0.00"
                  className="bg-gray-50 border-gray-300"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInitDialogOpen(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleInitializeLedger} disabled={processing || !initForm.store_id} className="bg-orange-600 hover:bg-orange-700 text-white">
              {processing ? 'Processing...' : 'Initialize Ledger'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FranchiseLedgerPage;
